import { Location } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { formatDate } from 'app/helpers/formatDate';
import { sortCompare } from 'app/helpers/sortCompare';
import { DropDownItem } from 'app/interfaces/items/dropDownItem';
import { ExposedReadUserDetailsRes } from 'app/interfaces/responses/Exposed/exposedReadUserDetailsRes';
import { ListAssistTypesConfigRes } from 'app/interfaces/responses/List/listAssistTypesConfigRes';
import { ReadAssistsConfigRes } from 'app/interfaces/responses/Read/readAssistsConfigRes';
import { JiraIssuesListRes } from 'app/interfaces/support/jira/jiraIssuesListRes';
import { JiraSearchIssuesIssuesDTO } from 'app/interfaces/support/jira/jiraSearchIssuesDTORes';
import { JiraService } from 'app/services/api/jira/jira.service';
import { LsrauthService } from 'app/services/api/lsrauth/lsrauth.service';
import { LoaderService } from 'app/services/system/loader/loader.service';
import { EaTabIndex, JiraEaViewMode, StatusFilterMode } from 'common/constants/assists-jira-ea.constants';

@Component({
    selector: 'app-assists-jira-ea',
    templateUrl: './assists-jira-ea.component.html',
    styleUrls: ['./assists-jira-ea.component.scss'],
})
export class AssistsJiraEAComponent implements OnInit, AfterViewInit {
    @ViewChild('sentinel', { static: false })
    sentinel!: ElementRef;

    files: File[] = [];
    issues: Array<JiraIssuesListRes> = [];
    allIssues: Array<JiraSearchIssuesIssuesDTO> = [];
    isLoading: boolean = true;
    // Keep track of if the initial fetch of issues is complete
    initialFetchComplete: boolean = false;

    selectedPanel: string = '';
    @Input() selectedPanelIndex: number = 0;
    private statusFilterMode: StatusFilterMode = 'open';
    private currentRequestId: number = 0;

    private observer!: IntersectionObserver;
    pageSize: number = 50;
    isLastPage: boolean = false;
    nextPageToken: string = null;
    userDetails: ExposedReadUserDetailsRes = null;
    currentStatusFilter: string[] = [];
    orgRequestsColumns: string[] = ['key', 'title', 'created', 'status'];
    displayedData: MatTableDataSource<JiraIssuesListRes> = new MatTableDataSource();

    listAssistTypes: Array<ListAssistTypesConfigRes>;
    assistTypes: Array<DropDownItem> = [];
    selectedAssistType: ListAssistTypesConfigRes;
    selectedAssistConfig: ReadAssistsConfigRes;

    constructor(
        public jiraService: JiraService,
        public loaderService: LoaderService,
        private location: Location,
        public lsrauthService: LsrauthService,
        public dialog: MatDialog
    ) {
        this.lsrauthService.userDetails.subscribe((userDetails) => (this.userDetails = userDetails));
    }

    ngOnInit(): void {
        this.findCorrectTabBasedOnUrl();
        this.initPanelBasedOnUrl();
    }

    ngAfterViewInit(): void {
        this.observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    if (this.nextPageToken && !this.isLoading) {
                        this.observer.unobserve(entry.target);
                        this.fetchIssues();
                    } else if (this.isLastPage) {
                        this.observer.disconnect();
                    }
                }
            });
        });
    }

    resetObserver() {
        if (this.observer) {
            this.observer.disconnect();
        }
        this.observer.observe(this.sentinel.nativeElement);
    }

    formatDate(date: string) {
        return formatDate(date);
    }

    // Empty the list and reset pagination
    resetList() {
        this.allIssues = [];
        this.issues = [];
        this.nextPageToken = null;
        this.isLastPage = false;
        this.displayedData.data = this.issues;
        this.initialFetchComplete = false;
    }

    fetchIssues() {
        this.isLoading = true;
        const eaViewMode = this.getEaViewModeForTab(this.selectedPanelIndex);
        const requestId = ++this.currentRequestId;

        this.jiraService
            .listIssues({
                NextPageToken: this.nextPageToken,
                PageSize: this.pageSize,
                Project: 'EA',
                Ssn: null,
                EaViewMode: eaViewMode,
            })
            .subscribe(
                (res) => {
                    if (requestId !== this.currentRequestId) {
                        return;
                    }

                    this.isLoading = false;
                    this.allIssues = this.allIssues.concat(res.issues);

                    const filtered = this.applyFilters(this.allIssues);

                    this.issues = filtered.map((issue) => ({
                        key: issue.key,
                        title: issue.fields?.summary ?? '',
                        created: issue.fields?.created ?? '',
                        reporterName: issue.fields.reporter.displayName,
                        status: issue.fields?.status?.name ?? '',
                        url: 'https://lsr.atlassian.net/servicedesk/customer/portal/8/' + issue.key,
                        ownerName: issue.fields.customfield_10079?.displayName,
                        implementerName: issue.fields.customfield_10081?.displayName,
                        eaDate: issue.fields.customfield_10082,
                        assistAmount: null,
                        assistAllocationDate: null,
                    }));

                    this.displayedData.data = this.issues;
                    this.isLastPage = res.isLast;
                    this.nextPageToken = res.nextPageToken;
                    this.initialFetchComplete = true;
                    this.resetObserver();
                },
                (error) => {
                    if (requestId !== this.currentRequestId) {
                        return;
                    }
                    console.error('Error fetching issue:', error);
                    this.isLoading = false;
                }
            );
    }

    //#region Table Sort and Filter

    private readonly OPEN_STATUSES = ['Móttekið', 'Í vinnslu'];

    private applyFilters(issues: JiraSearchIssuesIssuesDTO[]): JiraSearchIssuesIssuesDTO[] {
        return issues.filter((issue) => {
            const statusName = issue.fields?.status?.name ?? '';

            switch (this.statusFilterMode) {
                case 'all':
                    return true;
                case 'open':
                    return this.OPEN_STATUSES.includes(statusName);
                case 'closed':
                    return !this.OPEN_STATUSES.includes(statusName);
            }
        });
    }

    sortData(sort: Sort) {
        const data = this.displayedData.data.slice();
        if (!sort.active || sort.direction === '') {
            this.displayedData.data = data;
            return;
        }

        this.displayedData.data = data.sort((a, b) => {
            const isAsc = sort.direction === 'asc';
            switch (sort.active) {
                case 'key':
                    return sortCompare(a.key, b.key, isAsc);
                case 'title':
                    return sortCompare(a.title, b.title, isAsc);
                case 'created':
                    return sortCompare(a.created, b.created, isAsc);
                case 'status':
                    return sortCompare(a.status, b.status, isAsc);
                default:
                    return 0;
            }
        });
    }

    //#endregion

    private applyTabConfig(index: number) {
        switch (index) {
            case EaTabIndex.TilFramkvaemdar:
                this.selectedPanel = 'til-framkvaemdar';
                this.statusFilterMode = 'open';
                break;

            case EaTabIndex.Loknar:
                this.selectedPanel = 'loknar';
                this.statusFilterMode = 'closed';
                break;

            case EaTabIndex.Allar:
                this.selectedPanel = 'allar';
                this.statusFilterMode = 'all';
                break;

            case EaTabIndex.Eigandi:
                this.selectedPanel = 'eigandi';
                this.statusFilterMode = 'all';
                break;
        }

        this.location.go(`/atvik-og-beidnir/eftirlitsadgerdir/${this.selectedPanel}`);
    }

    findCorrectTabBasedOnUrl() {
        try {
            this.selectedPanel = window.location.href.split('/').pop();
        } catch {}
    }

    initPanelBasedOnUrl() {
        const lastSegment = this.selectedPanel;

        if (lastSegment === 'loknar') {
            this.selectedPanelIndex = EaTabIndex.Loknar;
        } else if (lastSegment === 'allar') {
            this.selectedPanelIndex = EaTabIndex.Allar;
        } else if (lastSegment === 'eigandi') {
            this.selectedPanelIndex = EaTabIndex.Eigandi;
        } else {
            this.selectedPanelIndex = EaTabIndex.TilFramkvaemdar;
            this.selectedPanel = 'til-framkvaemdar';
        }

        this.applyTabConfig(this.selectedPanelIndex);
        this.fetchIssues();
    }

    tabChanged(event: MatTabChangeEvent) {
        this.resetList();
        this.selectedPanelIndex = event.index;
        this.applyTabConfig(event.index);
        this.fetchIssues();
    }

    private getEaViewModeForTab(index: number): JiraEaViewMode {
        switch (index) {
            case EaTabIndex.TilFramkvaemdar:
            case EaTabIndex.Loknar:
                return JiraEaViewMode.ImplementerNonYfirmal; // same JQL

            case EaTabIndex.Allar:
                return JiraEaViewMode.ImplementerYfirmal;

            case EaTabIndex.Eigandi:
                return JiraEaViewMode.OwnerYfirmal;

            default:
                return JiraEaViewMode.Default;
        }
    }
}
